import { testColors } from "@mesketch/design-tokens";
import { Bell, DoneMark, TestIcon } from "@mesketch/svg";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router";
import { api, type CatalogItem, type OverviewSession } from "../data/api.js";
import { device, type SessionRecord } from "../data/device.js";
import { useIdentity } from "../data/identity.js";
import { standingOf } from "../data/standing.js";
import { knownToneOf, toneOf } from "../components/content.js";
import { HomeExamples, PortraitExample } from "../components/examples.js";
import { ErrorBox, useHomeTitle } from "../components/Layout.js";
import { T, TextsProvider } from "../components/texts.js";

// Domů (REQ-017, CR-019, CR-026): šest sekcí pod sebou jako jedna cesta očima. Úvod s hlavním
// tlačítkem na první nehotový test hlavní sekce a ukázkou výsledku, „Kde se ti to hodí“, testy
// hlavní sekce, obraz jako bonus, vedlejší sekce (placený test, nástroje) a závěr s druhým hlavním
// tlačítkem. Sekci určuje API (CR-019 §2). Stav testu ukazuje ikona, bere se ze sezení uložených
// v zařízení a na přihlášeném zařízení i ze sezení identity (REQ-033). Karta hotového testu vede
// rovnou na výsledek (REQ-041). Test bez publikované verze je „brzy“.

export interface TestProgress {
  readonly variant: "color" | "sketch";
  readonly progress: number;
  readonly session: SessionRecord | null;
}

/** Stav testu podle sezení v zařízení: hotové barevně, rozpracované částečně, jinak skica. */
export function progressOf(slug: string, sessions: readonly SessionRecord[]): TestProgress {
  const mine = sessions.filter((s) => s.slug === slug && s.origin === "user");
  const done = mine.find((s) => s.done === true);
  if (done !== undefined) return { variant: "color", progress: 1, session: done };
  const open = mine.at(-1);
  if (open === undefined) return { variant: "sketch", progress: 0, session: null };
  return { variant: "sketch", progress: open.total > 0 ? open.answered / open.total : 0, session: open };
}

const me = (chunks: React.ReactNode[]) => <span className="me-word">{chunks}</span>;

/** Test hlavního tlačítka: první dostupný test hlavní sekce, který zařízení nemá hotový. */
export function startOf(main: readonly CatalogItem[], sessions: readonly SessionRecord[]): CatalogItem | null {
  const open = main.filter((m) => m.available && m.startable);
  return open.find((m) => progressOf(m.slug, sessions).session?.done !== true) ?? open[0] ?? null;
}

/**
 * Hlavní tlačítko (CR-024 §1): text testu `methodology.start`, pod ním délka a „zdarma“. Je na
 * stránce dvakrát, nahoře a na konci (CR-026 §1).
 */
function StartButton({ item, testId = "home-start" }: { item: CatalogItem; testId?: string }) {
  const intl = useIntl();
  const t = (k: string) => item.texts[k]?.value;
  return (
    <Link className="button hero__cta" to={`/${intl.locale}/test/${item.slug}`} data-testid={testId} data-slug={item.slug}>
      <span className="hero__cta-label">{t("methodology.start") ?? intl.formatMessage({ id: "home.start.fallback" }, { name: t("methodology.name") ?? item.slug })}</span>
      <span className="hero__cta-meta">
        <FormattedMessage id="home.start.meta" values={{ duration: t("methodology.duration") ?? "" }} />
      </span>
    </Link>
  );
}

export function HomeHero({ start }: { start: CatalogItem | null }) {
  return (
    <section className="hero">
      <div className="hero__text">
        <p className="hero__eyebrow">
          <FormattedMessage id="home.eyebrow" />
        </p>
        <h1>
          <FormattedMessage id="home.title" values={{ me }} />
        </h1>
        <p className="hero__lead">
          <FormattedMessage id="home.subtitle" />
        </p>
        <div className="hero__start" data-tone={start === null ? undefined : (knownToneOf(start.slug) ?? undefined)}>
          {start !== null ? <StartButton item={start} /> : <span className="hero__cta-space" aria-hidden="true" />}
        </div>
      </div>
      <SampleCard />
    </section>
  );
}

/**
 * Ukázka výsledku (CR-025 §1, CR-026 §2): karta škály jako na Skice emocí se statickými daty
 * a texty rozhraní, v rohu malý obraz jako bonus. Štítek se na mobilu liší (karta je tam až pod
 * tlačítkem).
 */
function SampleCard() {
  const intl = useIntl();
  return (
    <aside className="sample card" data-tone="emotion" data-testid="home-sample" aria-labelledby="home-sample-scale">
      <PortraitExample index={0} className="sample__image" />
      <p className="sample__label">
        <span className="sample__label--mobile">
          <FormattedMessage id="home.sample.labelMobile" />
        </span>
        <span className="sample__label--desktop">
          <FormattedMessage id="home.sample.label" />
        </span>
      </p>
      <h2 className="sample__scale" id="home-sample-scale">
        <FormattedMessage id="home.sample.scale" />
      </h2>
      <Bell z={-0.62} ci={[-1.05, -0.2]} neutral={[-0.5, 0.5]} color={testColors.emotion.main} lowReliability={false} mostLabel={intl.formatMessage({ id: "sketch.portrait.bell.most" })} label={intl.formatMessage({ id: "home.sample.bell" })} />
      <p className="sample__sentence">
        <FormattedMessage id="home.sample.sentence" />
      </p>
      <p className="sample__text">
        <FormattedMessage id="home.sample.text" />
      </p>
    </aside>
  );
}

/** „Kde se ti to hodí“ (CR-026 §2): čtyři oblasti bez podkladu, linka v barvě sady testů. */
const WHERE = [
  ["relations", "emotion"],
  ["work", "work"],
  ["emotions", "strength"],
  ["decisions", "who"],
] as const;

/** Nadpis sekce s perexem (CR-026 §1). */
function SectionHead({ id, title, lead }: { id: string; title: ReactNode; lead: ReactNode }) {
  return (
    <div className="home-head">
      <h2 id={id}>{title}</h2>
      <p className="home-head__lead">{lead}</p>
    </div>
  );
}

function WhereItHelps() {
  return (
    <section aria-labelledby="home-where" className="home-section home-where" data-testid="home-where">
      <SectionHead id="home-where" title={<FormattedMessage id="home.where.title" />} lead={<FormattedMessage id="home.where.lead" />} />
      <ul className="where">
        {WHERE.map(([k, tone]) => (
          <li key={k} className="where__item" data-tone={tone}>
            <h3>
              <FormattedMessage id={`home.where.${k}.title`} />
            </h3>
            <p>
              <FormattedMessage id={`home.where.${k}.text`} />
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** „Obraz, který nikdo jiný mít nebude“ (CR-026 §2): jediná barevná plocha stránky. */
function PictureSection() {
  return (
    <section aria-labelledby="home-picture" className="home-picture" data-testid="home-picture">
      <div className="home-picture__text">
        <p className="home-eyebrow">
          <FormattedMessage id="home.picture.eyebrow" />
        </p>
        <h2 id="home-picture">
          <FormattedMessage id="home.picture.title" />
        </h2>
        <p className="home-head__lead">
          <FormattedMessage id="home.picture.text" />
        </p>
      </div>
      <HomeExamples />
    </section>
  );
}

/**
 * Počet testů hlavní sekce v předrenderovaném HTML (nadpis testů), než katalog odpoví: dnešní
 * počet, aby se text po načtení neměnil a stránka neposkočila (NFR-003, CLS). Jiný počet
 * z katalogu text opraví.
 */
const PRERENDER_MAIN = 3;

/** Stav testu jako text: hotovo (s výsledkem „zobrazit výsledek“), rozpracováno, nebo nic. */
function StateLabel({ state, result }: { state: TestProgress; result: string | null }) {
  if (result !== null) return <FormattedMessage id="home.test.doneOpen" />;
  if (state.session?.done === true) return <FormattedMessage id="home.test.done" />;
  return state.session !== null ? <FormattedMessage id="home.test.inProgress" /> : null;
}

/**
 * Karta testu hlavní sekce (CR-026 §2): ikona podle stavu, název, krátký popis, pod čarou délka
 * a „Začít →“, u rozpracovaného a hotového testu jeho stav. Hotový test vede rovnou na výsledek
 * a má v rohu zelenou fajfku (REQ-041). První nehotový test má rámeček.
 */
function TestCard({ item, state, result, first }: { item: CatalogItem; state: TestProgress; result: string | null; first: boolean }) {
  const { locale } = useIntl();
  const tone = toneOf(item.slug);
  const done = result !== null || state.session?.done === true;
  const body = (
    <>
      {done ? (
        <span className="test-card__done" data-testid="card-done">
          <DoneMark />
        </span>
      ) : null}
      <TestIcon tone={tone} variant={state.variant} progress={state.progress} size={40} />
      <span className="test-card__body">
        <span className="test-card__name">
          <T k="methodology.name" />
        </span>
        {item.available ? (
          <span className="test-card__text">{item.texts["methodology.short"] !== undefined ? <T k="methodology.short" /> : <T k="methodology.claim" />}</span>
        ) : null}
        <span className="test-card__meta">
          {!item.available ? (
            <FormattedMessage id="home.test.soon" />
          ) : (
            <>
              <span>
                <T k="methodology.duration" />
                {item.rounds >= 3 ? (
                  <>
                    {" · "}
                    <FormattedMessage id="home.layer.rounds" values={{ rounds: item.rounds }} />
                  </>
                ) : null}
              </span>
              <span className="test-card__go" data-testid="card-go">
                {state.session !== null ? (
                  <StateLabel state={state} result={result} />
                ) : (
                  <>
                    <FormattedMessage id="home.test.go" /> <span aria-hidden="true">→</span>
                  </>
                )}
              </span>
            </>
          )}
        </span>
      </span>
    </>
  );
  const cls = `test-card${first ? " test-card--first" : ""}${done ? " test-card--done" : ""}`;
  return (
    <TextsProvider texts={item.texts} locale={locale} preview={false}>
      {item.available ? (
        <Link className={cls} to={result ?? `/${locale}/test/${item.slug}`} data-testid={`test-${item.slug}`} data-tone={knownToneOf(item.slug) ?? undefined} data-first={first ? "true" : undefined}>
          {body}
        </Link>
      ) : (
        <div className={cls} data-testid={`test-${item.slug}`} data-soon="true">
          {body}
        </div>
      )}
    </TextsProvider>
  );
}

/**
 * Karta vedlejší sekce (CR-019 §6, CR-026 §2): bílá karta s ikonou testu, krátkým popisem (bez
 * něj `methodology.claim`), „Placený test, {délka}.“ a odkazem „Co dostanu →“ na úvod testu.
 */
function SideCard({ item, state, result }: { item: CatalogItem; state: TestProgress; result: string | null }) {
  const { locale } = useIntl();
  const tone = knownToneOf(item.slug);
  const nameId = `side-${item.slug}-name`;
  return (
    <TextsProvider texts={item.texts} locale={locale} preview={false}>
      <article className="side-card" data-tone={tone ?? undefined} data-testid={`side-${item.slug}`} data-soon={item.available ? undefined : "true"}>
        {tone !== null ? <TestIcon tone={tone} variant={state.variant} progress={state.progress} size={40} /> : null}
        <div className="side-card__body">
          <h3 id={nameId}>
            <T k="methodology.name" />
          </h3>
          {item.available ? (
            <>
              <p>
                {item.texts["methodology.short"] !== undefined ? <T k="methodology.short" /> : <T k="methodology.claim" />}{" "}
                <span data-testid="side-badge">
                  <FormattedMessage id={item.entry === "paid" ? "home.side.metaPaid" : "home.side.metaFree"} values={{ duration: <T k="methodology.duration" /> }} />
                </span>
                {state.session !== null ? (
                  <>
                    {" "}
                    <StateLabel state={state} result={null} />
                  </>
                ) : null}
              </p>
              <Link className="side-card__link" to={result ?? `/${locale}/test/${item.slug}`} aria-describedby={nameId}>
                <FormattedMessage id={result !== null ? "home.side.result" : "home.side.what"} /> <span aria-hidden="true">→</span>
              </Link>
            </>
          ) : (
            <p className="side-card__soon">
              <FormattedMessage id="home.test.soon" />
            </p>
          )}
        </div>
      </article>
    </TextsProvider>
  );
}

export function HomePage() {
  const intl = useIntl();
  const lang = intl.locale;
  const [catalog, setCatalog] = useState<readonly CatalogItem[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [local, setLocal] = useState<readonly SessionRecord[]>([]);
  const [remote, setRemote] = useState<readonly OverviewSession[]>([]);
  const identity = useIdentity();
  useHomeTitle();

  const load = useCallback(() => {
    setFailed(false);
    api.catalog(lang).then(
      (r) => setCatalog(r.methodologies),
      () => setFailed(true),
    );
  }, [lang]);

  useEffect(() => {
    load();
    void (async () => {
      setLocal(await device.sessions().catch(() => [] as SessionRecord[]));
      if (identity.kind !== "in") {
        setRemote([]);
        return;
      }
      setRemote(
        await api.identityOverview().then(
          (o) => o.sessions,
          () => [],
        ),
      );
    })();
  }, [load, identity.kind]);

  // Sezení identity z jiných zařízení: postup po kolech (REQ-033).
  const known = new Set(local.map((s) => s.id));
  const sessions: readonly SessionRecord[] = [
    ...local,
    ...remote
      .filter((s) => !known.has(s.id))
      .map((s): SessionRecord => ({ id: s.id, token: "", slug: s.slug, locale: s.locale, origin: "user", createdAt: s.started_at, answered: s.rounds_done, total: s.rounds, done: s.status === "done" })),
  ];
  const resultOf = (slug: string) => standingOf(slug, lang, local, remote).done?.href ?? null;

  // Neuložené hotové výsledky zařízení bez přihlášení (REQ-033, Libor 19. 10. 2026).
  const unsaved = identity.kind === "anon" ? sessions.filter((s) => s.origin === "user" && s.done === true && s.saved !== true).length : 0;
  const main = catalog?.filter((item) => item.section === "main") ?? [];
  const side = catalog?.filter((item) => item.section === "side") ?? [];
  const start = startOf(main, sessions);
  return (
    <div className="home">
      <HomeHero start={start} />
      {unsaved > 0 ? (
        <p className="notice notice--quiet home-unsaved" data-testid="home-unsaved">
          <FormattedMessage id="home.unsaved" values={{ count: unsaved }} />{" "}
          <Link to={`/${lang}/me`}>
            <FormattedMessage id="home.unsavedAction" />
          </Link>
        </p>
      ) : null}
      <WhereItHelps />
      <section aria-labelledby="home-free-title" className="home-section home-tests" id="home-free">
        <SectionHead
          id="home-free-title"
          title={<FormattedMessage id="home.free.title" values={{ count: catalog === null ? PRERENDER_MAIN : main.filter((m) => m.available).length }} />}
          lead={<FormattedMessage id="home.free.lead" />}
        />
        {catalog === null ? (
          failed ? <ErrorBox onRetry={load} /> : null
        ) : main.length === 0 ? (
          <p className="muted">
            <FormattedMessage id="home.empty" />
          </p>
        ) : (
          <ul className="test-cards" data-testid="catalog">
            {main.map((item) => (
              <li key={item.slug}>
                <TestCard item={item} state={progressOf(item.slug, sessions)} result={resultOf(item.slug)} first={item.slug === start?.slug} />
              </li>
            ))}
          </ul>
        )}
      </section>
      <PictureSection />
      {side.length > 0 ? (
        <section aria-labelledby="home-side" className="home-section home-deeper" data-testid="catalog-side">
          <SectionHead id="home-side" title={<FormattedMessage id="home.deeper.title" />} lead={<FormattedMessage id="home.deeper.text" />} />
          <ul className="side-list">
            {side.map((item) => (
              <li key={item.slug}>
                <SideCard item={item} state={progressOf(item.slug, sessions)} result={resultOf(item.slug)} />
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      <section className="home-end" data-testid="home-trust">
        <p>
          <FormattedMessage id="home.trust.text" />
        </p>
        {start !== null ? (
          <div data-tone={knownToneOf(start.slug) ?? undefined}>
            <StartButton item={start} testId="home-start-end" />
          </div>
        ) : null}
      </section>
    </div>
  );
}
